import { useEffect, useRef, type ReactNode } from "react";
import {
  useForm,
  type DefaultValues,
  type SubmitHandler,
  type UseFormReturn,
  type FieldValues,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ZodType } from "zod";
import { useDirtyPrompt } from "@/hooks/use-dirty-prompt";

export interface EntityFormProps<T extends FieldValues> {
  schema: ZodType<T, T>;
  defaultValues: DefaultValues<T>;
  onSubmit: SubmitHandler<T>;
  autosave?: (values: T) => Promise<void>;
  autosaveDelayMs?: number;
  children: (form: UseFormReturn<T>) => ReactNode;
}

export function EntityForm<T extends FieldValues>({
  schema,
  defaultValues,
  onSubmit,
  autosave,
  autosaveDelayMs = 2000,
  children,
}: EntityFormProps<T>) {
  const form = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onBlur",
  });

  // Reading isDirty and isValid during render subscribes this component to those
  // formState fields via RHF's proxy, ensuring the refs below are always current.
  const { isDirty, isValid } = form.formState;
  useDirtyPrompt(isDirty);

  // Keep refs so the setTimeout callback can read the latest render values
  // without needing to re-close over formState on every watch tick.
  const isDirtyRef = useRef(isDirty);
  const isValidRef = useRef(isValid);
  isDirtyRef.current = isDirty;
  isValidRef.current = isValid;

  // Single-timer debounce: form.watch's callback return value is NOT a lifecycle
  // hook, so we manage the timer via a ref. Each value change clears the prior
  // timer and starts a new one; the effect cleanup also clears any in-flight
  // timer so a late autosave never fires after unmount.
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (!autosave) return;
    const sub = form.watch((values) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (isDirtyRef.current && isValidRef.current) {
          // values is DeepPartial<T> from RHF watch; the isValid guard ensures
          // the schema has accepted the snapshot, so the cast is safe in practice.
          autosave(values as T).catch(() => undefined);
        }
      }, autosaveDelayMs);
    });
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      sub.unsubscribe();
    };
  }, [form, autosave, autosaveDelayMs]);

  // void-wrap: handleSubmit returns a Promise; the wrapper prevents the
  // @typescript-eslint/no-misused-promises rule from rejecting the JSX prop.
  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} noValidate>
      {children(form)}
    </form>
  );
}
